import { api } from '../axios.helper';
import { Config, TraineeDto } from '@ecdlink/core';
import {
  CmsVisitDataInputModelInput,
  SsChecklistVisitModelInput,
  SupportVisitModelInput,
  TraineeAddressModelInput,
  TraineeOnBoardTimeline,
  UpdateVisitPlannedVisitDateModelInput,
  UserConsentInput,
  Visit,
  VisitData,
} from '@ecdlink/graphql';

class TraineeService {
  _accessToken: string;

  constructor(accessToken: string) {
    this._accessToken = accessToken;
  }

  async getTraineeByUserId(userId: string): Promise<TraineeDto> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'traineeByUserId',
      variables: {
        userId: userId,
      },
    });

    if (response.status !== 200) {
      throw new Error('Get Trainee by id Failed - Server connection error');
    }

    return response.data.data.traineeByUserId;
  }

  async getTraineeTimeline(userId: string): Promise<TraineeOnBoardTimeline> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'GetOnBoardTraineeTimeline',
      variables: {
        userId,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Get Trainee onboard timeline Failed - Server connection error'
      );
    }

    return response.data.data.onBoardTraineeTimeline;
  }

  async getVisitDataForVisitId(visitId: string): Promise<VisitData[]> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<any>(``, {
      id: 'GetVisitDataForVisitId',
      variables: {
        visitId: visitId,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'Get Trainee visit data for visit id Failed - Server connection error'
      );
    }

    return response.data.data.visitDataForVisitId;
  }

  async updateCommunitySupport(
    userId: string,
    haveCommunitySupport: boolean
  ): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { addVisitData: boolean };
      errors?: {};
    }>(``, {
      id: 'UpdateCommunitySupport',
      variables: {
        userId,
        haveCommunitySupport,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Add update community support failed - Server connection error'
      );
    }

    return true;
  }

  async updateConsolidationMeeting(
    userId: string,
    attendedConsolidationMeeting: boolean,
    consolidatingMeetingDate: Date
  ): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { addVisitData: boolean };
      errors?: {};
    }>(``, {
      id: 'UpdateConsolidationMeeting',
      variables: {
        userId,
        attendedConsolidationMeeting,
        consolidatingMeetingDate,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Add update consolidation meeting attended failed - Server connection error'
      );
    }

    return true;
  }

  async signFranchisorAgreement(
    id: string,
    input: UserConsentInput
  ): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { addVisitData: boolean };
      errors?: {};
    }>(``, {
      id: 'updateUserConsent',
      variables: {
        id,
        input,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Add update Franchisor agreement support failed - Server connection error'
      );
    }

    return true;
  }

  async promoteTraineeAndRemoveNotifications(userId: string): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);

    const response = await apiInstance.post<{
      data: { promoteTraineeAndRemoveNotifications: boolean };
      errors?: {};
    }>(``, {
      id: 'promoteTraineeAndRemoveNotifications',
      variables: { userId },
    });

    if (response.status !== 200) {
      throw new Error(
        'Promote Trainee And Remove Notifications failed - Server connection error'
      );
    }

    const { data, errors } = response.data;

    if (errors) {
      console.error(errors);
      throw new Error('GraphQL error occurred while promoting trainee.');
    }

    return data?.promoteTraineeAndRemoveNotifications === true;
  }

  async addSSChecklistForTrainee(
    input: SsChecklistVisitModelInput
  ): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { addVisitData: boolean };
      errors?: {};
    }>(``, {
      id: 'AddSSChecklistForTrainee',
      variables: {
        input,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Add SS checklist for trainee failed - Server connection error'
      );
    }

    return true;
  }

  async addCoachVisitData(
    input: CmsVisitDataInputModelInput
  ): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { addVisitData: boolean };
      errors?: {};
    }>(``, {
      query: `
      mutation AddCoachVisitData($input: CMSVisitDataInputModelInput) {
        addCoachVisitData(input: $input) {
        }
    }
      `,
      variables: {
        input,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Add update community support failed - Server connection error'
      );
    }

    return true;
  }

  async editVisitData(input: CmsVisitDataInputModelInput): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { addVisitData: boolean };
      errors?: {};
    }>(``, {
      id: 'EditVisitData',
      variables: {
        input,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Add update community support failed - Server connection error'
      );
    }

    return true;
  }

  async addStartupSupportAgreementForTrainee(
    input: SupportVisitModelInput
  ): Promise<boolean> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { addVisitData: boolean };
      errors?: {};
    }>(``, {
      id: 'AddStartupSupportAgreementForTrainee',
      variables: {
        input,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Add update community support failed - Server connection error'
      );
    }

    return true;
  }

  async UpdateTraineeAddress(
    userId: string,
    input: TraineeAddressModelInput
  ): Promise<any> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { updateTraineeAddress: any };
      errors?: {};
    }>(``, {
      id: 'UpdateTraineeAddress',
      variables: {
        userId,
        input,
      },
    });

    if (response.status !== 200) {
      throw new Error(
        'change trainee smart space address failed - Server connection error'
      );
    }

    return response.data.data.updateTraineeAddress;
  }

  async updateTraineeOnboardTimelineSSVisitEvent(
    input: UpdateVisitPlannedVisitDateModelInput
  ): Promise<Visit> {
    const apiInstance = api(Config.graphQlApi, this._accessToken);
    const response = await apiInstance.post<{
      data: { updateVisitPlannedVisitDate: Visit };
      errors?: {};
    }>(``, {
      id: 'updateVisitPlannedVisitDate',
      variables: {
        input,
      },
    });

    if (response.status !== 200 || response.data.errors) {
      throw new Error(
        'Update Visit PlannedVisitDate failed - Server connection error'
      );
    }

    return response.data.data.updateVisitPlannedVisitDate;
  }
}

export default TraineeService;
